import { useRef, useCallback, useEffect, useState } from 'react'
import { NumberRange, NumericAxis, DateTimeNumericAxis } from 'scichart'

type ChartAxis = NumericAxis | DateTimeNumericAxis

export interface ChartSyncRegistration {
  /** Unique identifier for the chart */
  chartId: string
  /** SciChart X-axis instance to synchronize */
  xAxis: ChartAxis
}

export interface UseChartSyncReturn {
  /** Register a chart's X-axis for synchronization */
  registerChart: (registration: ChartSyncRegistration) => void
  /** Unregister a chart from synchronization */
  unregisterChart: (chartId: string) => void
  /** Whether synchronization is currently enabled */
  isEnabled: boolean
  /** Enable or disable synchronization */
  setEnabled: (enabled: boolean) => void
  /** Number of currently registered charts */
  registeredCount: number
}

/**
 * Hook for managing X-axis synchronization between multiple charts
 * 
 * Provides a registration system where individual chart components can
 * register their X-axes to participate in synchronized zooming/panning.
 * 
 * @param initialEnabled - Whether synchronization starts enabled
 * @returns Object with registration functions and sync control
 */
export function useChartSync(initialEnabled: boolean = true): UseChartSyncReturn {
  const [isEnabled, setIsEnabled] = useState(initialEnabled)
  const [registeredCount, setRegisteredCount] = useState(0)
  
  // Store registered charts and their axes
  const registrationsRef = useRef<Map<string, ChartSyncRegistration>>(new Map())
  
  // Store active subscriptions for cleanup
  const subscriptionsRef = useRef<Array<() => void>>([])
  
  // Store setup timeout for cleanup
  const setupTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Clears all existing subscriptions
   */
  const clearSubscriptions = useCallback(() => {
    subscriptionsRef.current.forEach(unsubscribe => {
      try {
        unsubscribe()
      } catch (error) {
        console.warn('Error unsubscribing from chart sync:', error)
      }
    })
    subscriptionsRef.current = []
  }, [])

  /**
   * Sets up synchronization between all registered axes
   */
  const setupSynchronization = useCallback(() => {
    if (!isEnabled) {
      clearSubscriptions()
      return
    }

    // Clear existing subscriptions
    clearSubscriptions()

    const registrations = Array.from(registrationsRef.current.values())
    const axes = registrations.map(reg => reg.xAxis).filter(Boolean)
    
    // Need at least 2 axes to synchronize
    if (axes.length < 2) {
      return
    }

    console.log(`Setting up X-axis sync for ${axes.length} charts`)

    // Subscribe each axis to sync with others
    axes.forEach((sourceAxis, sourceIndex) => {
      const syncHandler = (data: unknown) => {
        if (!data || typeof data !== 'object' || !('visibleRange' in data)) {
          return
        }

        const visibleRange = (data as any).visibleRange
        if (!visibleRange || typeof visibleRange.min !== 'number' || typeof visibleRange.max !== 'number') {
          return
        }

        const newRange = new NumberRange(visibleRange.min, visibleRange.max)
        
        // Update all other axes
        axes.forEach((targetAxis, targetIndex) => {
          if (targetIndex !== sourceIndex && targetAxis !== sourceAxis) {
            try {
              targetAxis.visibleRange = newRange
            } catch (error) {
              console.warn(`Failed to sync chart ${targetIndex}:`, error)
            }
          }
        })
      }
      
      // Subscribe to visible range changes
      try {
        sourceAxis.visibleRangeChanged.subscribe(syncHandler)
        
        // Store cleanup function
        subscriptionsRef.current.push(() => {
          sourceAxis.visibleRangeChanged.unsubscribe(syncHandler)
        })
      } catch (error) {
        console.warn('Failed to subscribe to visibleRangeChanged:', error)
      }
    })
  }, [isEnabled, clearSubscriptions])

  /**
   * Registers a chart's X-axis for synchronization
   */
  const registerChart = useCallback((registration: ChartSyncRegistration) => {
    if (!registration.chartId || !registration.xAxis) {
      console.warn('Invalid chart registration:', registration)
      return
    }

    // Store the registration
    registrationsRef.current.set(registration.chartId, registration)
    setRegisteredCount(registrationsRef.current.size)
    
    console.log(`Registered chart ${registration.chartId} for X-axis sync`)

    // Clear any pending setup
    if (setupTimeoutRef.current) {
      clearTimeout(setupTimeoutRef.current)
    }

    // Setup synchronization with a small delay to ensure all charts are ready
    setupTimeoutRef.current = setTimeout(() => {
      setupSynchronization()
      setupTimeoutRef.current = null
    }, 100)
  }, [setupSynchronization])

  /**
   * Unregisters a chart from synchronization
   */
  const unregisterChart = useCallback((chartId: string) => {
    if (!chartId) {
      console.warn('Invalid chartId for unregistration:', chartId)
      return
    }

    const wasRegistered = registrationsRef.current.has(chartId)
    registrationsRef.current.delete(chartId)
    setRegisteredCount(registrationsRef.current.size)
    
    if (wasRegistered) {
      console.log(`Unregistered chart ${chartId} from X-axis sync`)
      
      // Clear any pending setup
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current)
        setupTimeoutRef.current = null
      }

      // Re-setup synchronization with remaining charts
      setupTimeoutRef.current = setTimeout(() => {
        setupSynchronization()
        setupTimeoutRef.current = null
      }, 100)
    }
  }, [setupSynchronization])

  /**
   * Enable or disable synchronization
   */
  const setEnabled = useCallback((enabled: boolean) => {
    setIsEnabled(enabled)
    
    if (enabled) {
      // Re-setup synchronization when enabled
      setupSynchronization()
    } else {
      // Clear subscriptions when disabled
      clearSubscriptions()
    }
  }, [setupSynchronization, clearSubscriptions])

  // Re-setup synchronization when enabled state changes
  useEffect(() => {
    if (isEnabled && registrationsRef.current.size > 1) {
      setupSynchronization()
    } else if (!isEnabled) {
      clearSubscriptions()
    }
  }, [isEnabled, setupSynchronization, clearSubscriptions])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending setup
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current)
      }
      
      // Clear all subscriptions
      clearSubscriptions()
      
      // Clear registrations
      registrationsRef.current.clear()
    }
  }, [clearSubscriptions])

  return {
    registerChart,
    unregisterChart,
    isEnabled,
    setEnabled,
    registeredCount
  }
}

export default useChartSync 